import { chakra } from "@chakra-ui/react";
import React from "react";

// Chakra port of the PDF stylesheet (medflyt-serverless renderDocument.ts getStyle) for one block.

export const PRINT_FONT_FAMILY = `"Open Sans", "Segoe UI", "Helvetica Neue", Arial, sans-serif`;
export const PRINT_MUTED_COLOR = "#545454";
export const PRINT_INK_COLOR = "#292929";
const HANDWRITING_FONT_FAMILY = `"Brush Script MT", "Segoe Script", "Lucida Handwriting", cursive`;

/** Root of every printed block: 12px Open Sans like the PDF body. */
export const PrintBlock = chakra("div", {
  baseStyle: {
    color: "black",
    fontFamily: PRINT_FONT_FAMILY,
    fontSize: "12px",
    lineHeight: "normal",
    width: "100%",
  },
});

/** `.block-inline-title`: question title above a 2px underline. */
export const InlineTitle = chakra("div", {
  baseStyle: {
    borderBottom: "2px solid",
    fontWeight: 600,
    lineHeight: "25px",
    textAlign: "start",
  },
});

/** `.block-title`: grey rounded bar used for headers and table titles. */
export const BarTitle = chakra("div", {
  baseStyle: {
    bg: "#d9d9d994",
    borderRadius: "8px",
    fontWeight: 600,
    px: "12px",
    py: "6px",
  },
});

export const MutedNote = chakra("div", {
  baseStyle: {
    color: PRINT_MUTED_COLOR,
    fontStyle: "italic",
    py: "4px",
  },
});

export const Handwriting = chakra("span", {
  baseStyle: {
    fontFamily: HANDWRITING_FONT_FAMILY,
    fontSize: "28px",
    // Slanted even where no handwriting font is installed and `cursive` falls back to a serif.
    fontStyle: "italic",
    lineHeight: 1.2,
  },
});

interface TitledBlockProps {
  children: React.ReactNode;
  /** gap between the title and the content in block (non-inline) display */
  gap: string;
  /** `answerInline`: "Title: answer" on one line above a 2px underline */
  isInline: boolean;
  title: string;
}

/** Title + content block (Text, Radios, Checkboxes, StartEndTime) in block or inline display. */
export function TitledBlock(props: TitledBlockProps) {
  if (props.isInline) {
    return (
      <PrintBlock
        alignItems="baseline"
        borderBottom="2px solid"
        display="flex"
        flexDirection="row"
        gap={props.gap}
        pb="1px"
        pt="8px"
      >
        <chakra.div flexShrink={0} fontWeight={600} lineHeight="25px">
          {props.title}:
        </chakra.div>
        <chakra.div display="flex" flexGrow={1} flexWrap="wrap" gap="0 32px">
          {props.children}
        </chakra.div>
      </PrintBlock>
    );
  }

  return (
    <PrintBlock display="flex" flexDirection="column" gap={props.gap}>
      <InlineTitle>{props.title}</InlineTitle>
      {props.children}
    </PrintBlock>
  );
}
